import uri from 'urijs';
import { Response } from 'node-fetch';

import { GraphNode, GraphTree, LimitedGraphTree } from './model';
import { prodBackend, wsProtocol } from './constants';
import { leaveQueryBreadCrumb } from '.';
import { ObjectType } from '@vromo/models/helpers/models';

/** Return Error object description ( if any ) */
export const parseError = (payload: Error | string): string => {
    const errorData = (payload as Error).message || payload;
    if (typeof errorData === 'string' && errorData) {
        return payload.toString();
    }

    return 'Error: undefined';
};

export type GraphErrorType = InstanceType<typeof GraphError>;

/** rest api Error ( includes http response error code ) */
export class GraphError extends Error {
    protected _code: number;

    constructor(statusCode: number, payload: Error | string) {
        super(
            (payload as Error).message ||
                (payload || `response status '${statusCode}', undetailed error`).toString()
        );
        this._code = statusCode;
    }

    get code(): number {
        return this._code;
    }
}

/** Extract and format received data from vromo rest api */
export const handleResponse = async <T>(queryReponse: Response, scrubbedPath = ''): Promise<T> => {
    const { ok, status, headers } = queryReponse;

    if (!ok) {
        const jsonResponse: any =
            parseInt(headers.get('content-length') as string) > 0 ? await queryReponse.json() : '';
        return Promise.reject(
            new GraphError(
                status,
                [
                    jsonResponse.toString() || queryReponse.statusText,
                    scrubbedPath && ` on [${scrubbedPath}] endpoint`
                ].join('')
            )
        );
    }

    return (await queryReponse.json()) as T;
};

/** widely common http rest queries */
const httpQuery = {
    get: async <T = unknown>(
        url: string,
        queryParams: ObjectType,
        token: string,
        scrubbedPath = ''
    ): Promise<T> => {
        const targetUrl = uri(url)
            .query({ ...(token && { tok: token }), ...queryParams })
            .toString();
        const queryResponse = await fetch(targetUrl, { method: 'GET' });

        return handleResponse<T>(queryResponse as Response, scrubbedPath);
    },

    post: async <T = unknown>(
        url: string,
        data: T[],
        token: string,
        scrubbedPath = ''
    ): Promise<T> => {
        const requestPath = uri(url)
            .query({ ...(token && { tok: token }) })
            .toString();
        leaveQueryBreadCrumb({ data, path: requestPath, type: 'request', protocol: 'post' });
        const queryResponse = await fetch(requestPath, {
            method: 'POST',
            body: JSON.stringify(data)
        });
        return handleResponse<T>(queryResponse as Response, scrubbedPath);
    },

    patch: async <T = unknown>(
        url: string,
        data: Partial<T>[],
        token: string,
        scrubbedPath = ''
    ): Promise<Partial<T>> => {
        const requestPath = uri(url)
            .query({ ...(token && { tok: token }) })
            .toString();
        leaveQueryBreadCrumb({ data, path: requestPath, type: 'request', protocol: 'patch' });
        const queryResponse = await fetch(requestPath, {
            method: 'PATCH',
            body: JSON.stringify(data)
        });
        return handleResponse<Partial<T>>(queryResponse as Response, scrubbedPath);
    },

    delete: async <T = unknown>(
        url: string,
        queryParams: ObjectType,
        token: string,
        scrubbedPath = ''
    ): Promise<T> => {
        const requestPath = uri(url)
            .query({ ...(token && { tok: token }), ...queryParams })
            .toString();
        const queryResponse = await fetch(requestPath, {
            method: 'DELETE'
        });
        return handleResponse<T>(queryResponse as Response, scrubbedPath);
    }
};

export function endpoint<T = unknown>(name: string, children: any[] = []): unknown {
    const node: GraphNode = {
        token: '',
        name,
        edges: [],
        url() {
            return `${process.env.DONT_USE_SSL ? 'http' : 'https'}://${
                this.host
            }/v2/${this.edges.join('/')}`;
        },
        scrubbedPath() {
            return this.edges.map((item) => (typeof item === 'number' ? '[id]' : item)).join('/');
        },
        curl(body: ObjectType) {
            return body
                ? `curl ${this.url()}`
                : `curl -X POST ${this.url()} --data "${JSON.stringify(body).replace(
                      new RegExp('"', 'g'),
                      '\\"'
                  )}"`;
        },
        socketUrl(customToken: string) {
            const tok = customToken || this.token;
            const basePath = `${wsProtocol}://${this.socketHost}/sockets/${this.edges.join('/')}`;
            return tok ? `${basePath}?access_token=${tok}` : basePath;
        },
        get(queryParams: ObjectType, customToken = '') {
            return httpQuery.get<T>(
                this.url(),
                queryParams,
                customToken || this.token,
                this.scrubbedPath()
            );
        },
        post(data: T[], customToken = '') {
            return httpQuery.post<T>(
                this.url(),
                data,
                customToken || this.token,
                this.scrubbedPath()
            );
        },
        patch(data: Partial<T>[], customToken = '') {
            return httpQuery.patch<Partial<T>>(
                this.url(),
                data,
                customToken || this.token,
                this.scrubbedPath()
            );
        },
        delete(queryParams: ObjectType, customToken = '') {
            return httpQuery.delete<T>(
                this.url(),
                queryParams,
                customToken || this.token,
                this.scrubbedPath()
            );
        }
    };

    children.forEach((child) => {
        // each Graph node/endpoint, may have others graph children nodes/ endpoints
        // 'ids' represents the possible ids used when querying a resource, for instance, a site: '...graph().sites(1234).get()'
        node[child.name] = function (...ids: (string | number)[]) {
            return {
                ...child,
                edges: ids.length
                    ? [...this.edges, child.name, ...ids.map((id) => id?.toString() || '')].filter(
                          (item) => item
                      )
                    : [...this.edges, child.name],
                host: this.host,
                socketHost: this.socketHost,
                token: this.token
            };
        };
    });

    return node as GraphNode<T>;
}

/** collection of api operations for limited user*/
export const LIMITED_GRAPHSCHEMA_LIVESITE = endpoint('root', [
    endpoint('graph', [endpoint('dashboard'), endpoint('jobs')])
]) as LimitedGraphTree;

/** collection of api operations for general user */
export const DEFAULT_SCHEMA = endpoint('root', [
    endpoint('graph', [
        endpoint('roles', [
            endpoint('dashboard'),
            endpoint('policies'),
            endpoint('campaigns'),
            endpoint('groups'),
            endpoint('jobs', [
                endpoint('offered'),
                endpoint('dispatches'),
                endpoint('tasks'),
                endpoint('uploads'),
                endpoint('chat'),
                endpoint('policy', [endpoint('simulation')])
            ]),
            endpoint('vehicles', [endpoint('points'), endpoint('driver')]),
            endpoint('account', [endpoint('roles')]),
            endpoint('ratings'),
            endpoint('reports', [endpoint('engagement'), endpoint('vehicle')]),
            endpoint('campaigns'),
            endpoint('sites', [endpoint('jobs')]),
            endpoint('blueprints'),
            endpoint('deliveryServiceProviders', [
                endpoint('availabilityForFleets'),
                endpoint('deliveryServiceProviderActivation')
            ])
        ]),
        endpoint('sessions'),
        endpoint('user', [endpoint('contact'), endpoint('uploads'), endpoint('avatar')]),
        endpoint('driveables', [
            endpoint('jobs', [endpoint('dispatches'), endpoint('tasks')]),
            endpoint('points')
        ]),
        endpoint('error404')
    ]),
    endpoint('logout'),
    endpoint('track', [
        endpoint('job', [
            endpoint('tasks'),
            endpoint('ratings'),
            endpoint('contact', [endpoint('channels')])
        ])
    ])
]) as GraphTree;

/** By default returns current user api operations schema */
export class Graph {
    private _root: GraphTree;
    private _limited: LimitedGraphTree;

    constructor(host = prodBackend, token?: string) {
        this._root = DEFAULT_SCHEMA;
        this._limited = LIMITED_GRAPHSCHEMA_LIVESITE;
        this._root.host = host;
        this._root.socketHost = process.env.WS_API || host;
        this._limited.host = host;
        this._limited.socketHost = this._root.socketHost;
        if (token) {
            this._root.token = token;
            this._limited.token = token;
        }
    }

    /** graph standard user api operations */
    get root(): typeof this._root {
        return this._root;
    }

    /** graph limited user api operations */
    get limited(): typeof this._limited {
        return this._limited;
    }

    /** current configured vromo access token */
    get token(): string {
        return this._root.token;
    }
}

declare global {
    interface Window {
        Graph: Graph;
    }
}
